import React, { useState, useEffect } from "react";
import {
  Box, Button, Card, CardContent, CardMedia,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, IconButton, TextField, Typography, MenuItem, Switch,
  FormControlLabel, Container, Divider, Checkbox, List, ListItem, ListItemText,
  FormControl, InputLabel, Select
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RestaurantMenuIcon from "@mui/icons-material/RestaurantMenu";
import FileCopyIcon from "@mui/icons-material/FileCopy";
import { useLocation } from "react-router-dom";
import { db } from "../../firebase";
import { collection, addDoc, getDocs, deleteDoc, doc, updateDoc, query, where, getDoc } from "firebase/firestore";
import Alerts from "../../components/Alerts";
import { menuService } from "../../services/menuService";



const CATEGORIES = ["Starters", "Main Course", "Beverages", "Desserts", "Others"];

const formatPriceForUI = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isInteger(num) ? num.toString() : num.toString();
};

const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const generateItemCode = (name) => {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  let code = words
    .map(word => word[0])
    .join("")
    .toUpperCase();

  return code.slice(0, 5); // max length 5
};

export default function ManageMenu() {
// Example: src/pages/MenuManager.js



  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const companyCodeParam = queryParams.get("code");
  const unitCodeParam = queryParams.get("unit");
  const isPureVegParam = queryParams.get("isPureVeg") === "true";
  const [originalGstPlan, setOriginalGstPlan] = useState("");
  const [companyCode, setCompanyCode] = useState(companyCodeParam);
  const [unitCode, setUnitCode] = useState(unitCodeParam);
  const [isPureVeg, setIsPureVeg] = useState(isPureVegParam);
  const [gstPlans, setGstPlans] = useState([]);
  const [menuTimings, setMenuTimings] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editItemId, setEditItemId] = useState(null);
  const [items, setItems] = useState([]);
  const [kitchenStaff, setKitchenStaff] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [detailDialog, setDetailDialog] = useState({ open: false, item: null });
  const [importDialog, setImportDialog] = useState({ open: false, items: [], selected: [] });
  const [pendingImportItem, setPendingImportItem] = useState(null);
  const [priceCategories, setPriceCategories] = useState([]);
  const [categoryPrices, setCategoryPrices] = useState({});
  const [categories, setCategories] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    itemCode: "",
    category: "",
    description: "",
    ingredients: "",
    price: "",
    type: "veg",
    available: true,
    timing: "",
    imageUrl: "",
    staff: "",
    gstPlan: ""
  });
  const [gstUpdateDialog, setGstUpdateDialog] = useState({
    open: false,
    applyToCategory: false,
  });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });

  const fetchCategories = async () => {
  try {
    let q;

    if (unitCode) {
      q = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", unitCode)
      );
    } else {
      q = query(
        collection(db, "categories"),
        where("companyCode", "==", companyCode)
      );
    }

    const snapshot = await getDocs(q);

    const list = snapshot.docs.map(doc => doc.data().name);

    setCategories(list);

  } catch (error) {
    console.error("Error fetching categories:", error);
  }
};
  useEffect(() => {
    const initialize = async () => {
      if (unitCode) {
        try {
          const unitDocRef = doc(db, "units", unitCode);
          const unitDoc = await getDoc(unitDocRef);
          if (unitDoc.exists()) {
            const unitData = unitDoc.data();
            setCompanyCode(unitData.companyCode);
            // Fetch isPureVeg from company if not provided
            if (!isPureVegParam) {
              const companyDocRef = doc(db, "companies", unitData.companyCode);
              const companyDoc = await getDoc(companyDocRef);
              if (companyDoc.exists()) {
                setIsPureVeg(companyDoc.data().isPureVeg || false);
              }
            }
          } else {
            setSnackbar({ open: true, message: "Invalid unit code.", severity: "error" });
            return;
          }
        } catch (error) {
          console.error("Error validating unit:", error);
          setSnackbar({ open: true, message: "Failed to validate unit.", severity: "error" });
          return;
        }
      } else if (!companyCode) {
        setSnackbar({ open: true, message: "Company or unit code missing. Some features may not work correctly.", severity: "warning" });
        return;
      }

      fetchGstPlans();
      setItems( await menuService.fetchCompanyMenuItems()||[]);
      fetchKitchenStaff();
      fetchPriceCategories();
        fetchCategories();
        fetchMenuTimings();
    };

    initialize();
  }, [companyCode, unitCode, isPureVegParam]);

  useEffect(() => {
  if (companyCode) {
    fetchMenuTimings();
  }
}, [companyCode, unitCode]);

  const fetchGstPlans = async () => {
    try {
      const gstQuery = query(collection(db, "gstPlans"), where("companyCode", "==", companyCode));
      const gstSnapshot = await getDocs(gstQuery);
      const gstList = gstSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setGstPlans(gstList);
      console.log(`ManageMenu: Fetched ${gstList.length} GST plans for companyCode: ${companyCode}`);
      if (gstList.length === 0) {
        console.warn("ManageMenu: No GST plans found for companyCode:", companyCode);
      }
    } catch (error) {
      console.error("ManageMenu: Error fetching GST plans:", error);
      setSnackbar({ open: true, message: "Failed to load GST plans.", severity: "error" });
    }
  };

  

  const fetchKitchenStaff = async () => {
    try {
      const q = query(collection(db, "users"), where("role", "==", "kitchen_staff"), where("companyCode", "==", companyCode));
      const snapshot = await getDocs(q);
      const prefix = unitCode || companyCode;
      const staff = snapshot.docs
        .map((doc) => doc.data().userId)
        .filter((userId) => userId.startsWith(`${prefix}#`));
      setKitchenStaff(staff);
    } catch (error) {
      console.error("Error fetching kitchen staff:", error);
      setSnackbar({ open: true, message: "Failed to load kitchen staff.", severity: "error" });
    }
  };

  const fetchMasterMenu = async () => {
    try {
      const filterField = unitCode ? "unitCode" : "companyCode";
      const filterValue = unitCode || companyCode;
      const companyQuery = query(collection(db, "companymenu"), where(filterField, "==", filterValue));
      const companySnapshot = await getDocs(companyQuery);
      const companyItemNames = companySnapshot.docs.map((doc) => doc.data().name);

      const snapshot = await getDocs(collection(db, "menu"));
      let data = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((item) => !companyItemNames.includes(item.name));

      if (isPureVeg) {
        data = data.filter((item) => item.type === "veg" || item.category === "Beverages");
      }

      setImportDialog((prev) => ({ ...prev, items: data, selected: [] }));
    } catch (error) {
      console.error("Error fetching master menu:", error);
      setSnackbar({ open: true, message: "Failed to load master menu.", severity: "error" });
    }
  };

  //Price Categories
   /* const fetchPriceCategories = async () => {
      try {
        const q = query(
          collection(db, "priceCategories"),
          where("companyCode", "==", companyCode)
        );
        const snapshot = await getDocs(q);
        const list = snapshot.docs.map(doc => doc.data());
        setPriceCategories(list);
      } catch (error) {
        console.error("Error fetching price categories:", error);
      }
    };*/
    const fetchPriceCategories = async () => {
  try {

    let q;

    if (unitCode) {
      // fetch unit specific categories
      q = query(
        collection(db, "priceCategories"),
        where("companyCode", "==", companyCode),
        where("unitCode", "==", unitCode)
      );
    } else {
      // fallback to company categories
      q = query(
        collection(db, "priceCategories"),
        where("companyCode", "==", companyCode)
      );
    }

    const snapshot = await getDocs(q);

    const list = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setPriceCategories(list);

  } catch (error) {
    console.error("Error fetching price categories:", error);
  }
};
  
useEffect(() => {
  if (companyCode) {
    fetchPriceCategories();
  }
}, [companyCode, unitCode]);

    useEffect(() => {
      if (!open) return; 
      const basePrice = Number(formData.price);
      if (!basePrice || isNaN(basePrice)) {
        setCategoryPrices({});
        return;
      }
  
      const calculatedPrices = {};
  
      priceCategories.forEach(cat => {
        if (cat.percentageIncrement > 0) {
          calculatedPrices[cat.priceCategoryName] = Number(
            basePrice + (basePrice * cat.percentageIncrement) / 100
          ).toFixed(2);
        }
      });
  
      // DO NOT merge old values
      setCategoryPrices(prev => {
        const manualPrices = {};
  
        priceCategories.forEach(cat => {
          if (cat.percentageIncrement === 0 && prev[cat.priceCategoryName]) {
            manualPrices[cat.priceCategoryName] = prev[cat.priceCategoryName];
          }
        });
  
        return {
          ...calculatedPrices,
          ...manualPrices,
        };
      });
    }, [formData.price, priceCategories]);

  const handleOpen = (category = "") => {
    setFormData({
      name: "",
      itemCode: "",
      category: category,
      description: "",
      ingredients: "",
      price: "",
      type: isPureVeg ? "veg" : "veg",
      available: true,
      timing: "",
      imageUrl: "",
      staff: "",
      gstPlan: ""
    });
    setCategoryPrices({});
    setIsEditing(false);
    setEditItemId(null);
    setOpen(true);
  };

  const handleEditClick = (item, isImport = false) => {
    setIsEditing(!isImport);
    setEditItemId(isImport ? null : item.id);
    setFormData({
      name: item.name,
      itemCode: item.itemCode || "",
      category: item.category,
      description: item.description || "",
      ingredients: item.ingredients || "",
      price: item.price.toString(), // Use stored price (includes GST for non-inclusive plans)
      type: item.type,
      available: item.available,
      timing: item.timing || "",
      imageUrl: item.imageUrl || "",
      staff: item.staff || "",
      gstPlan: item.gstPlan || ""
    });

    // Store original GST plan
    setOriginalGstPlan(item.gstPlan || "");

    const formattedCategoryPrices = {};

    Object.entries(item.categoryPrices || {}).forEach(([key, value]) => {
      formattedCategoryPrices[key] = formatPriceForUI(value);
    });

    setCategoryPrices(formattedCategoryPrices);
    setDetailDialog({ open: false, item: null });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setIsEditing(false);
    setEditItemId(null);
    setFormData({
      name: "",
      itemCode: "",
      category: "",
      description: "",
      ingredients: "",
      price: "",
      type: isPureVeg ? "veg" : "veg",
      available: true,
      timing: "",
      imageUrl: "",
      staff: "",
      gstPlan: ""
    });
    setPendingImportItem(null);
  };

  const handleImportOpen = () => {
    if (!unitCode) {
      setSnackbar({ open: true, message: "Cannot import menu without a unit code.", severity: "error" });
      return;
    }
    fetchMasterMenu();
    setImportDialog((prev) => ({ ...prev, open: true }));
  };

  const handleImportClose = () => {
    setImportDialog({ open: false, items: [], selected: [] });
  };

  const handleImportSelect = (itemId) => {
    setImportDialog((prev) => ({
      ...prev,
      selected: prev.selected.includes(itemId)
        ? prev.selected.filter((id) => id !== itemId)
        : [...prev.selected, itemId],
    }));
  };

  const handleImportConfirm = async () => {
    try {
      const selectedItems = importDialog.items.filter((item) =>
        importDialog.selected.includes(item.id)
      );
      if (selectedItems.length === 0) {
        setSnackbar({ open: true, message: "No items selected for import.", severity: "warning" });
        return;
      }

      const firstItem = selectedItems[0];
      setPendingImportItem({
        items: selectedItems.slice(1),
        current: firstItem,
      });
      handleEditClick(firstItem, true);
      setImportDialog((prev) => ({ ...prev, open: false, selected: [] }));
    } catch (error) {
      console.error("Error initiating import:", error);
      setSnackbar({ open: true, message: "Failed to initiate import.", severity: "error" });
    }
  };

  const isItemCodeExists = async (itemCode) => {
    if (!itemCode) return false;
  
    const q = query(
      collection(db, "companymenu"),
      where("companyCode", "==", companyCode),
      where("itemCode", "==", itemCode)
    );
  
    const snapshot = await getDocs(q);
  
    // If editing, ignore current item
    if (isEditing) {
      return snapshot.docs.some(doc => doc.id !== editItemId);
    }
  
    return !snapshot.empty;
  };

  const calculateGSTValues = (price, gstPlan) => {
  const enteredPrice = parseFloat(price);

  let finalPrice = enteredPrice;
  let basePrice = enteredPrice;
  const gstPercentage = gstPlan.percentage;

  let cgstAmount = 0;
  let sgstAmount = 0;

  if (gstPlan.inclusive) {
    basePrice = enteredPrice * 100 / (100 + gstPercentage);

    const totalGstAmount = enteredPrice - basePrice;

    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;

    finalPrice = enteredPrice;
  } else {
    basePrice = enteredPrice;

    const totalGstAmount = enteredPrice * (gstPercentage / 100);

    cgstAmount = totalGstAmount / 2;
    sgstAmount = totalGstAmount / 2;

    finalPrice = enteredPrice + totalGstAmount;
  }

  return {
    price: parseFloat(finalPrice.toFixed(2)),
    basePrice: parseFloat(basePrice.toFixed(2)),
    gstPercentage,
    cgstAmount: parseFloat(cgstAmount.toFixed(2)),
    sgstAmount: parseFloat(sgstAmount.toFixed(2)),
  };
};
  const handleAdd = async () => {
    try {
      if (!companyCode) {
        setSnackbar({ open: true, message: "Cannot add item without a company code.", severity: "error" });
        return;
      }
      if (!formData.name || !formData.category || !formData.price || !formData.staff || !formData.gstPlan) {
        setSnackbar({
          open: true,
          message: "Please fill in required fields: Name, Category, Price, Staff User ID, and GST Plan",
          severity: "warning",
        });
        return;
      }

      // Check duplicate itemCode
const codeExists = await isItemCodeExists(formData.itemCode);

if (codeExists) {
  setSnackbar({
    open: true,
    message: "Item Code already exists. Please use a different code.",
    severity: "error",
  });
  return;
}

      if (isPureVeg && formData.type === "nonveg") {
        setSnackbar({
          open: true,
          message: "Cannot add non-veg items for a pure veg company.",
          severity: "warning",
        });
        return;
      }

      const enteredPrice = parseFloat(formData.price);
      if (enteredPrice <= 0) {
        setSnackbar({
          open: true,
          message: "Price must be greater than zero.",
          severity: "warning",
        });
        return;
      }

      const selectedGstPlan = gstPlans.find(plan => plan.gstName === formData.gstPlan);
      if (!selectedGstPlan) {
        setSnackbar({
          open: true,
          message: "Selected GST plan not found.",
          severity: "error",
        });
        return;
      }

      let finalPrice = enteredPrice;
      let basePrice = enteredPrice;
      const gstPercentage = selectedGstPlan.percentage;
      let cgstAmount = 0;
      let sgstAmount = 0;

      if (selectedGstPlan.inclusive) {
        // For inclusive GST, calculate base price and derive CGST/SGST
        basePrice = enteredPrice * 100 / (100 + gstPercentage);
        finalPrice = enteredPrice; // Store entered price as-is
        const totalGstAmount = enteredPrice - basePrice;
        cgstAmount = totalGstAmount / 2; // Half of GST as CGST
        sgstAmount = totalGstAmount / 2; // Half of GST as SGST
      } else {
        // For non-inclusive GST, add GST to the entered price and split into CGST/SGST
        basePrice = enteredPrice; // Entered price is the base price
        const totalGstAmount = enteredPrice * (gstPercentage / 100);
        cgstAmount = totalGstAmount / 2; // Half of GST as CGST
        sgstAmount = totalGstAmount / 2; // Half of GST as SGST
        finalPrice = enteredPrice + totalGstAmount; // Final price includes GST
      }

      //price categories handling
      const finalCategoryPrices = {};

      /*finalCategoryPrices["Standard Price"] = Number(basePrice).toFixed(2);*/

      /*priceCategories.forEach(cat => {
        finalCategoryPrices[cat.priceCategoryName] = Number(
          categoryPrices[cat.priceCategoryName] ?? basePrice
        ).toFixed(2);
      });*/

      finalCategoryPrices["Standard Price"] = Number(finalPrice).toFixed(2);

      priceCategories
        .filter(cat => cat.priceCategoryName !== "Standard Price")
        .forEach(cat => {
          finalCategoryPrices[cat.priceCategoryName] = Number(
            categoryPrices[cat.priceCategoryName] ?? basePrice
          ).toFixed(2);
        });

      const newItem = {
        name: formData.name,
        itemCode: formData.itemCode,
        category: formData.category,
        description: formData.description || "",
        ingredients: formData.ingredients || "",
        price: parseFloat(finalPrice.toFixed(2)),
        basePrice: parseFloat(basePrice.toFixed(2)),
        categoryPrices: finalCategoryPrices,
        type: formData.type,
        available: formData.available,
        timing: formData.available ? formData.timing : "",
        imageUrl: formData.imageUrl || "",
        staff: formData.staff,
        companyCode,
        ...(unitCode && { unitCode }),
        gstPlan: formData.gstPlan,
        gstPercentage,
        cgstAmount: parseFloat(cgstAmount.toFixed(2)), // Store CGST amount
        sgstAmount: parseFloat(sgstAmount.toFixed(2)), // Store SGST amount
        createdAt: new Date(),
      };

      /*if (isEditing) {
        await updateDoc(doc(db, "companymenu", editItemId), newItem);
        setSnackbar({ open: true, message: "Menu item updated successfully!", severity: "success" });
      }*/ 
     if (isEditing) {

  // GST changed
  if (originalGstPlan !== formData.gstPlan) {

    setGstUpdateDialog({
      open: true,
      applyToCategory: false,
      item: newItem,
    });

    return;
  }

  await updateDoc(doc(db, "companymenu", editItemId), newItem);

  setSnackbar({
    open: true,
    message: "Menu item updated successfully!",
    severity: "success"
  });

}else {
        await addDoc(collection(db, "companymenu"), newItem);
        setSnackbar({ open: true, message: "Menu item added successfully!", severity: "success" });
      }

      if (pendingImportItem && pendingImportItem.items.length > 0) {
        const nextItem = pendingImportItem.items[0];
        setPendingImportItem({
          items: pendingImportItem.items.slice(1),
          current: nextItem,
        });
        handleEditClick(nextItem, true);
      } else {
        handleClose();
        setItems(await menuService.fetchCompanyMenuItems() || []);
      }
    } catch (error) {
      console.error("Error saving item:", error);
      setSnackbar({ open: true, message: `Failed to save menu item: ${error.message}`, severity: "error" });
    }
  };

  const updateCategoryGST = async (applyToCategory) => {
  try {

    const item = gstUpdateDialog.item;

    // update current item first
    await updateDoc(doc(db, "companymenu", editItemId), item);

    if (applyToCategory) {

      const q = query(
        collection(db, "companymenu"),
        where("companyCode", "==", companyCode),
        where("category", "==", formData.category)
      );

      const snapshot = await getDocs(q);

      const selectedGstPlan = gstPlans.find(
        plan => plan.gstName === formData.gstPlan
      );

      for (const document of snapshot.docs) {

        if (document.id === editItemId) continue;

        const data = document.data();

        const currentPrice = Number(data.price);

let basePrice = currentPrice;
let cgstAmount = 0;
let sgstAmount = 0;

if (selectedGstPlan.inclusive) {

  basePrice =
    currentPrice * 100 / (100 + selectedGstPlan.percentage);

  const totalGstAmount = currentPrice - basePrice;

  cgstAmount = totalGstAmount / 2;
  sgstAmount = totalGstAmount / 2;

} else {

  // keep selling price same
  basePrice =
    currentPrice / (1 + selectedGstPlan.percentage / 100);

  const totalGstAmount = currentPrice - basePrice;

  cgstAmount = totalGstAmount / 2;
  sgstAmount = totalGstAmount / 2;
}

await updateDoc(doc(db, "companymenu", document.id), {
  gstPlan: formData.gstPlan,
  gstPercentage: selectedGstPlan.percentage,

  // KEEP PRICE SAME
  price: parseFloat(currentPrice.toFixed(2)),

  basePrice: parseFloat(basePrice.toFixed(2)),
  cgstAmount: parseFloat(cgstAmount.toFixed(2)),
  sgstAmount: parseFloat(sgstAmount.toFixed(2)),
});
      }
    }

    setSnackbar({
      open: true,
      message: applyToCategory
        ? "GST updated for all items in category."
        : "GST updated only for this item.",
      severity: "success",
    });

    setGstUpdateDialog({
      open: false,
      applyToCategory: false,
    });

    handleClose();
    setItems(menuService.fetchCompanyMenuItems()||[]);

  } catch (error) {
    console.error(error);

    setSnackbar({
      open: true,
      message: "Failed to update GST.",
      severity: "error",
    });
  }
};

  const handleDeleteClick = (id) => {
    setDeleteDialog({ open: true, id });
  };

  /*const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, "companymenu", deleteDialog.id));
      fetchCompanyItems();
      setSnackbar({ open: true, message: "Item deleted successfully.", severity: "info" });
    } catch (error) {
      console.error("Error deleting item:", error);
      setSnackbar({ open: true, message: "Failed to delete item.", severity: "error" });
    } finally {
      setDeleteDialog({ open: false, id: null });
    }
  };*/
  const handleDeleteConfirm = async () => {
  try {
    // 1. Delete from Database (The "Single Source of Truth")
    await menuService.deleteMenuItem(deleteDialog.id);
   

    // 2. Update Local State (No re-fetch needed!)
    // We filter the existing 'items' array, which is already in memory
    setItems((prevItems) => {
      // Defensive check: ensure prevItems is an array
      const safeItems = Array.isArray(prevItems) ? prevItems : [];
      return safeItems.filter((item) => item.id !== deleteDialog.id);
    });

    setSnackbar({ open: true, severity: "success", message: "Deleted successfully." });
  } catch (error) {
    console.error("Delete failed:", error);
  } finally {
    setDeleteDialog({ open: false, id: null });
  }
};

  const handleDetailClick = (item) => {
    setDetailDialog({ open: true, item });
  };

  const handleDetailClose = () => {
    setDetailDialog({ open: false, item: null });
  };

  /*const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };*/

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;

  let newValue = type === "checkbox" ? checked : value;

  /*if (name === "name") {
    const titleName = toTitleCase(value);

    setFormData((prev) => ({
      ...prev,
      name: titleName,
      itemCode: generateItemCode(titleName)
    }));*/
   if (name === "name") {
  const titleName = toTitleCase(value);

  setFormData((prev) => ({
    ...prev,
    name: titleName,
    itemCode: titleName ? generateItemCode(titleName) : ""
  }));

  return;
}
   
   
  if (name === "available" && !checked) {
  setFormData(prev => ({
    ...prev,
    available: false,
    timing: ""
  }));
  return;
}

  if (name === "itemCode") {
    newValue = value.toUpperCase().slice(0, 5);
  }

  setFormData((prev) => ({
    ...prev,
    [name]: newValue
  }));
};


  const groupByCategory = (items) => {
    const grouped = {};
    items = items=== null?[]:items;
    categories.forEach((category) => {
      console.info("Category:")
      grouped[category] = items.filter((item) => item.category === category);
    });
    return grouped;
  };

  const itemsByCategory = groupByCategory(items);

  const truncateText = (text, maxLength) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  const selectedGstPlan = gstPlans.find(plan => plan.gstName === formData.gstPlan);

  const fetchMenuTimings = async () => {
  try {
    if (!unitCode && !companyCode) return;

    const q = query(
      collection(db, "preferences"),
      where("companyCode", "==", companyCode),
      where("unitCode", "==", unitCode),
      where("preferenceType", "==", "Menu Timing")
    );

    const snapshot = await getDocs(q);

    const timings = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    setMenuTimings(timings);

  } catch (error) {
    console.error("Error fetching menu timings:", error);
  }
};

  return (
    <Container maxWidth="lg">
      <Box py={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center">
            <RestaurantMenuIcon fontSize="large" sx={{ mr: 2 }} />
            <Typography variant="h4">Menu Manager</Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FileCopyIcon />}
            onClick={handleImportOpen}
          >
            Import Master Menu
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpen()}
          sx={{ mb: 3 }}
        >
          Add New Item
        </Button>

        {categories.map((category) => {
          const categoryItems = itemsByCategory[category];
          if (!categoryItems || categoryItems.length === 0) {
            return (
              <Box key={category} mb={4}>
                <Typography variant="h5" mb={2}>{category}</Typography>
                <Divider sx={{ mb: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Card
                      elevation={3}
                      sx={{
                        height: "380px",
                        width: "300px",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                      }}
                      onClick={() => handleOpen(category)}
                    >
                      <CardContent>
                        <AddIcon sx={{ fontSize: 60, color: "primary.main" }} />
                        <Typography variant="h6" color="text.secondary">
                          Add Item
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </Box>
            );
          }

          return (
            <Box key={category} mb={4}>
              <Typography variant="h5" mb={2}>{category}</Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                {categoryItems.map((item) => (
                  <Grid item xs={12} sm={6} md={4} key={item.id}>
                    <Card
                      elevation={3}
                      sx={{
                        height: "380px",
                        width: "300px",
                        display: "flex",
                        flexDirection: "column",
                        cursor: "pointer",
                      }}
                      onClick={(e) => {
                        if (!e.target.closest(".MuiIconButton-root")) {
                          handleDetailClick(item);
                        }
                      }}
                    >
                      <CardMedia
                        component="img"
                        height="180"
                        image={item.imageUrl || "/placeholder.png"}
                        alt={item.name}
                        sx={{ objectFit: "cover" }}
                      />
                      <CardContent sx={{ flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Typography variant="h6" gutterBottom>{item.name}</Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                bgcolor: item.type === "veg" ? "success.light" : "error.light",
                                px: 1,
                                py: 0.5,
                                width: "72px",
                                textAlign: "center",
                                borderRadius: 1,
                                color: "white",
                              }}
                            >
                              {item.type === "veg" ? "VEG" : "NON-VEG"}
                            </Typography>
                          </Box>
                          <Typography
                            color="text.secondary"
                            variant="body2"
                            gutterBottom
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                            }}
                          >
                            {truncateText(item.description, 50)}
                          </Typography>
                          {item.ingredients && (
                            <Typography
                              color="text.secondary"
                              variant="body2"
                              gutterBottom
                              sx={{
                                display: "-webkit-box",
                                WebkitLineClamp: 1,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden",
                              }}
                            >
                              <strong>Ingredients:</strong> {truncateText(item.ingredients, 30)}
                              {item.ingredients.length > 30 && (
                                <Typography
                                  component="span"
                                  color="primary"
                                  variant="body2"
                                  sx={{ cursor: "pointer" }}
                                >
                                  {" Read more"}
                                </Typography>
                              )}
                            </Typography>
                          )}
                        </Box>
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="center" mt={0}>
                            <Typography variant="h6" color="primary">₹{item.price}</Typography>
                            <Box>
                              <IconButton onClick={() => handleEditClick(item)} color="primary" size="small">
                                <EditIcon />
                              </IconButton>
                              <IconButton onClick={() => handleDeleteClick(item.id)} color="error" size="small">
                                <DeleteIcon />
                              </IconButton>
                            </Box>
                          </Box>
                          {!item.available && (
                            <Typography variant="body2" color="error" sx={{ mt: 1, fontStyle: "italic" }}>
                              Currently unavailable
                            </Typography>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
                <Grid item xs={12} sm={6} md={4}>
                  <Card
                    elevation={3}
                    sx={{
                      height: "380px",
                      width: "300px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                    onClick={() => handleOpen(category)}
                  >
                    <CardContent>
                      <AddIcon sx={{ fontSize: 60, color: "primary.main" }} />
                      <Typography variant="h6" color="text.secondary">
                        Add Item
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          );
        })}
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{isEditing ? "Edit Menu Item" : "Add Menu Item"}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label="Name"
            name="name"
            fullWidth
            value={formData.name}
            onChange={handleChange}
            required
          />
          <TextField
            margin="dense"
            label="Item Code"
            name="itemCode"
            fullWidth
            value={formData.itemCode}
            onChange={handleChange}
            inputProps={{ maxLength: 5 }}
            helperText="Auto-generated from name. Max 5 characters"
          />
          <TextField
            select
            margin="dense"
            label="Category"
            name="category"
            fullWidth
            value={formData.category}
            onChange={handleChange}
            required
          >
            {categories.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Description"
            name="description"
            fullWidth
            multiline
            rows={2}
            value={formData.description}
            onChange={handleChange}
          />
          <TextField
            margin="dense"
            label="Ingredients"
            name="ingredients"
            fullWidth
            value={formData.ingredients}
            onChange={handleChange}
            placeholder="Comma separated ingredients"
          />
          <FormControl fullWidth margin="dense" sx={{ mt: 2 }}>
            <InputLabel id="gst-plan-label">GST Plan</InputLabel>
            <Select
              labelId="gst-plan-label"
              name="gstPlan"
              value={formData.gstPlan}
              label="GST Plan"
              onChange={handleChange}
              required
            >
              {gstPlans.length > 0 ? (
                gstPlans.map(plan => (
                  <MenuItem key={plan.id} value={plan.gstName}>
                    {plan.gstName} ({plan.percentage}%)
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="" disabled>
                  No GST plans available
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {formData.gstPlan && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Inclusive: {selectedGstPlan?.inclusive ? "Yes" : "No"}
            </Typography>
          )}
          <TextField
            margin="dense"
            label="Price"
            name="price"
            type="number"
            fullWidth
            value={formData.price}
            onChange={handleChange}
            required
            InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
            helperText={formData.gstPlan && selectedGstPlan?.inclusive ? "Enter price inclusive of GST" : "Enter base price (GST will be added)"}
          />
          {priceCategories.length > 0 && Number(formData.price) > 0 && (
                      <Box mt={1}>
                        {/*{priceCategories.map(cat => {*/}
                        {priceCategories
  .filter(cat => cat.priceCategoryName !== "Standard Price")
  .map(cat => {
                         // const isManual = cat.percentageIncrement === 0;
                          return (
                           <TextField
                            key={cat.priceCategoryName}
                            margin="dense"
                            //label={`${cat.priceCategoryName} (${cat.percentageIncrement}%)`}
                              label={
  cat.percentageIncrement === 0
    ? cat.priceCategoryName
    : `${cat.priceCategoryName} (${cat.percentageIncrement}%)`
}
                            type="number"
                            fullWidth
                            //InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography> }}
                            value={categoryPrices[cat.priceCategoryName] ?? ""}
                            onChange={(e) => {
                              if (cat.percentageIncrement !== 0) return;
          
                              setCategoryPrices(prev => ({
                                ...prev,
                                [cat.priceCategoryName]: e.target.value,
                              }));
                            }}
                            //disabled={cat.percentageIncrement !== 0}
                            InputProps={{
                              startAdornment: (
                                <Typography sx={{ mr: 1 }}>₹</Typography>
                              ),
                              readOnly: cat.percentageIncrement !== 0,
                            }}
                            helperText={
                              cat.percentageIncrement === 0
                                ? "Enter price manually"
                                : "Auto-calculated from standard price"
                            }
                            sx={{ mb: 1 }}
                          />
                          );
                        })}
                      </Box>
                    )}
          <TextField
            select
            margin="dense"
            label="Type"
            name="type"
            fullWidth
            value={formData.type}
            onChange={handleChange}
            disabled={isPureVeg}
          >
            <MenuItem value="veg">Vegetarian</MenuItem>
            <MenuItem value="nonveg">Non-Vegetarian</MenuItem>
          </TextField>
          <TextField
            select
            margin="dense"
            label="Staff User ID"
            name="staff"
            fullWidth
            value={formData.staff}
            onChange={handleChange}
            required
            helperText={kitchenStaff.length === 0 ? "No kitchen staff available" : "Select a kitchen staff member"}
          >
            {kitchenStaff.map((userId) => (
              <MenuItem key={userId} value={userId}>
                {userId}
              </MenuItem>
            ))}
          </TextField>
          {formData.available && (
  <TextField
    select
    margin="dense"
    label="Menu Timing"
    name="timing"
    fullWidth
    value={formData.timing}
    onChange={handleChange}
    required
  >
    {menuTimings.map((timing) => (
      <MenuItem
        key={timing.id}
        value={timing.preferenceName}
      >
        {timing.preferenceName}
      </MenuItem>
    ))}
  </TextField>
)}
          <FormControlLabel
            control={<Switch checked={formData.available} name="available" onChange={handleChange} color="success" />}
            label="Available for Order"
          />
          <TextField
            margin="dense"
            label="Image URL"
            name="imageUrl"
            fullWidth
            value={formData.imageUrl}
            onChange={handleChange}
            placeholder="Enter image URL"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleAdd} variant="contained" color="primary">
            {isEditing ? "Update Item" : "Add Item"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this menu item?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>Cancel</Button>
          <Button color="error" onClick={handleDeleteConfirm}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detailDialog.open} onClose={handleDetailClose} maxWidth="md" fullWidth>
        <DialogTitle>{detailDialog.item?.name}
           {detailDialog.item?.itemCode && ` (${detailDialog.item?.itemCode})`}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} gap={2}>
            <Box flexShrink={0}>
              <CardMedia
                component="img"
                height="200"
                width="300"
                image={detailDialog.item?.imageUrl || "/placeholder.png"}
                alt={detailDialog.item?.name}
                sx={{ objectFit: "cover", borderRadius: 1 }}
              />
            </Box>
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Category:</strong> {detailDialog.item?.category}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {detailDialog.item?.type === "veg" ? "Vegetarian" : "Non-Vegetarian"}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Description:</strong> {detailDialog.item?.description || "No description available"}
              </Typography>
              {detailDialog.item?.ingredients && (
                <Typography variant="body1" gutterBottom>
                  <strong>Ingredients:</strong> {detailDialog.item?.ingredients}
                </Typography>
              )}
              <Typography variant="body1" gutterBottom>
                <strong>Price:</strong> ₹{detailDialog.item?.price} {detailDialog.item?.gstPlan ? `(${detailDialog.item?.gstPlan.includes('inclusive') ? 'Inclusive' : 'Exclusive'} of ${detailDialog.item?.gstPercentage}% GST)` : ''}
              </Typography>
              {detailDialog.item?.basePrice && (
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <strong>Base Price:</strong> ₹{detailDialog.item?.basePrice.toFixed(2)}
                </Typography>
              )}
              {detailDialog.item?.categoryPrices &&
                Object.keys(detailDialog.item.categoryPrices).length > 0 && (
                <Box>
                  {Object.entries(detailDialog.item.categoryPrices).map(
                    ([categoryName, price]) => (
                      <Typography
                        key={categoryName}
                        variant="body2"
                        color="text.secondary"
                        gutterBottom
                      >
                        <strong>{categoryName}:</strong> ₹{Number(price).toFixed(2)}
                      </Typography>
                    )
                  )}
                </Box>
              )}
              <Typography variant="body1" gutterBottom>
                <strong>Staff:</strong> {detailDialog.item?.staff || "Not assigned"}
              </Typography>
              {detailDialog.item?.gstPlan && (
                <Typography variant="body1" gutterBottom>
                  <strong>GST Plan:</strong> {detailDialog.item?.gstPlan} ({detailDialog.item?.gstPercentage}%)
                </Typography>
              )}
              {detailDialog.item?.cgstAmount && detailDialog.item?.sgstAmount && (
                <>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>CGST:</strong> ₹{detailDialog.item?.cgstAmount.toFixed(2)} ({(detailDialog.item?.gstPercentage / 2).toFixed(2)}%)
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>SGST:</strong> ₹{detailDialog.item?.sgstAmount.toFixed(2)} ({(detailDialog.item?.gstPercentage / 2).toFixed(2)}%)
                  </Typography>
                </>
              )}
              <Typography
                variant="body1"
                color={detailDialog.item?.available ? "success.main" : "error.main"}
              >
                <strong>Availability:</strong> {detailDialog.item?.available ? "Available" : "Currently unavailable"}
              </Typography>
              {detailDialog.item?.timing && (
                <Typography variant="body1" gutterBottom>
                  <strong>Menu Timing:</strong> {detailDialog.item.timing}
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDetailClose}>Close</Button>
          <Button
            onClick={() => handleEditClick(detailDialog.item)}
            variant="contained"
            color="primary"
          >
            Edit Item
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={importDialog.open} onClose={handleImportClose} maxWidth="md" fullWidth>
        <DialogTitle>Import Master Menu</DialogTitle>
        <DialogContent>
          {importDialog.items.length === 0 ? (
            <Typography>No new items available in the master menu.</Typography>
          ) : (
            <>
              <Typography variant="h6" gutterBottom>Select Items to Import</Typography>
              <List>
                {importDialog.items.map((item) => (
                  <ListItem key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      checked={importDialog.selected.includes(item.id)}
                      onChange={() => handleImportSelect(item.id)}
                    />
                    <ListItemText
                      primary={item.name}
                      secondary={`${item.category} - ${item.type === "veg" ? "Veg" : "Non-Veg"} - ₹${item.price}${item.staff ? ` - Staff: ${item.staff}` : ' - Staff: Not assigned'}`}
                    />
                  </ListItem>
                ))}
              </List>
              {importDialog.selected.length > 0 && (
                <Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1, bgcolor: 'grey.50' }}>
                  <Typography variant="h6" gutterBottom>Selected Items</Typography>
                  <List>
                    {importDialog.items
                      .filter((item) => importDialog.selected.includes(item.id))
                      .map((item) => (
                        <ListItem key={item.id} sx={{ display: 'flex', alignItems: 'center' }}>
                          <ListItemText primary={item.name} />
                          <IconButton
                            onClick={() => handleEditClick(item, true)}
                            color="primary"
                            size="small"
                          >
                            <EditIcon />
                          </IconButton>
                        </ListItem>
                      ))}
                  </List>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportClose}>Cancel</Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            color="primary"
            disabled={importDialog.selected.length === 0}
          >
            Import Selected
          </Button>
        </DialogActions>
      </Dialog>
      
      <Dialog
  open={gstUpdateDialog.open}
  onClose={() =>
    setGstUpdateDialog({
      open: false,
      applyToCategory: false,
    })
  }
>
  <DialogTitle>Update GST for Category?</DialogTitle>

  <DialogContent>
    <Typography>
      GST selection has changed.
      Do you want to update GST for all items in this category?
    </Typography>
  </DialogContent>

  <DialogActions>

    <Button
      onClick={() => updateCategoryGST(false)}
    >
      No
    </Button>

    <Button
      variant="contained"
      onClick={() => updateCategoryGST(true)}
    >
      Yes
    </Button>

  </DialogActions>
</Dialog>

      <Alerts
        snackbar={snackbar}
        handleClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        darkMode={false}
      />
    </Container>
  );
}